import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGame, readyUp, leaveGame, startGame } from '../api/games';
import { useGameWebSocket } from '../hooks/useWebSocket';
import '../styles/pages.css';

export default function RoomPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const lastRoomEventAt = useRef(0);

  const fetchGame = useCallback(async () => {
    try {
      const { data } = await getGame(id);
      setGame(data);
      if (data.status === 'in_progress') {
        navigate(`/games/${id}/play`);
      }
    } catch {
      setError('获取房间信息失败');
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchGame();
    const interval = setInterval(fetchGame, 3000);
    return () => clearInterval(interval);
  }, [fetchGame]);

  const handleWsMessage = useCallback((data) => {
    if (data.type === 'room.update' && data.game) {
      const eventAt = Date.parse(data.event_at || '');
      if (eventAt && eventAt < lastRoomEventAt.current) return;
      if (eventAt) lastRoomEventAt.current = eventAt;

      setGame(data.game);
      if (data.game.status === 'in_progress') {
        navigate(`/games/${id}/play`);
      }
    }
    if (data.type === 'state.update' && data.game_status === 'in_progress') {
      navigate(`/games/${id}/play`);
    }
  }, [id, navigate]);

  const { closeCode } = useGameWebSocket(id, handleWsMessage, { onOpen: fetchGame });

  useEffect(() => {
    if (closeCode === 4001) {
      setError('实时连接认证失败，请重新登录');
    } else if (closeCode === 4003) {
      setError('你不在这个房间中，无法接收实时更新');
    }
  }, [closeCode]);

  const handleReady = async () => {
    setLoading(true);
    try {
      await readyUp(id);
      await fetchGame();
    } catch (err) {
      setError(err.response?.data?.detail || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handleLeave = async () => {
    try {
      await leaveGame(id);
      navigate('/lobby');
    } catch (err) {
      setError(err.response?.data?.detail || '离开失败');
    }
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      await startGame(id);
      navigate(`/games/${id}/play`);
    } catch (err) {
      setError(err.response?.data?.detail || '开始游戏失败');
    } finally {
      setLoading(false);
    }
  };

  if (!game) return <div className="loading-page">{error || '加载中...'}</div>;

  const userId = Number(user?.id);
  const myPlayer = game.players.find(p => Number(p.user) === userId);
  const isOwner = Number(game.owner) === userId;
  const allReady = game.players.length >= 2 && game.players.every(p => p.is_ready);
  const emptySlots = Array.from({ length: game.max_players - game.players.length });

  return (
    <div className="room-page">
      <div className="room-header">
        <h2>等待室 · 房间 #{game.id}</h2>
        <button className="btn-secondary" onClick={handleLeave}>🚪 离开</button>
      </div>

      <div className="room-meta">
        <span>{game.players.length}/{game.max_players} 人</span>
        <span className={`status-badge status-${game.status}`}>
          {game.status === 'ready' ? '✓ 全部就绪' : '等待中'}
        </span>
      </div>

      {error && <p className="error-msg">{error}</p>}

      <div className="players-list">
        {game.players.map(p => (
          <div key={p.id} className={`room-player ${Number(p.user) === userId ? 'me' : ''}`}>
            <span className="room-player-name">
              {p.username}
              {Number(game.owner) === Number(p.user) && ' 👑'}
              {Number(p.user) === userId && ' (我)'}
            </span>
            <span className={`ready-badge ${p.is_ready ? 'is-ready' : 'not-ready'}`}>
              {p.is_ready ? '✓ 已准备' : '未准备'}
            </span>
          </div>
        ))}
        {emptySlots.map((_, i) => (
          <div key={`empty-${i}`} className="room-player empty">
            <span className="room-player-name">等待玩家加入...</span>
          </div>
        ))}
      </div>

      <div className="room-actions">
        {myPlayer && (
          <button
            className={`btn-ready ${myPlayer.is_ready ? 'is-ready' : ''}`}
            onClick={handleReady}
            disabled={loading}
          >
            {myPlayer.is_ready ? '取消准备' : '准备'}
          </button>
        )}
        {isOwner ? (
          <>
            <button
              className="btn-start"
              onClick={handleStart}
              disabled={!allReady || loading}
              title={!allReady ? '需要房间满员且所有玩家准备' : ''}
            >
              开始游戏 🚀
            </button>
            {!allReady && (
              <p className="start-hint">
                {game.players.length < game.max_players
                  ? `等待更多玩家加入（${game.players.length}/${game.max_players}）`
                  : game.players.some(p => !p.is_ready)
                    ? '等待所有玩家点击"准备"'
                    : ''}
              </p>
            )}
          </>
        ) : (
          <p className="start-hint">等待房主 👑 开始游戏</p>
        )}
      </div>
    </div>
  );
}
